import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _typeof from "@babel/runtime/helpers/esm/typeof";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["className", "placeholder", "status", "disabled", "refer", "suffix", "prefix", "label", "labelMode", "allowClear", "onChange", "onFocus", "onBlur", "size", "errors", "ErrorNode", "warnings", "type", "htmlType", "precision", "max", "min", "bordered"];
import { IconDelete16pxBold } from "@pionex-web-kit/icons";
import cns from "classnames";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";
import { getPrefixCls } from "../config";
import SizeContext from "../ConfigProvider/SizeContext";
import useMergedState from "../hack/rc-util/hooks/useMergedState";
import { resolveOnChange, triggerFocus } from "./hack";
import AInput from "./hack/Input";
import { Password } from "./Password";
import TextArea from "./TextArea";
import { numberFixed } from "./utils";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
var InternalInput = /*#__PURE__*/forwardRef(function Input(_ref, ref) {
  var _cns2;
  var _ref$className = _ref.className,
    className = _ref$className === void 0 ? "" : _ref$className,
    placeholder = _ref.placeholder,
    status = _ref.status,
    disabled = _ref.disabled,
    refer = _ref.refer,
    suffix = _ref.suffix,
    prefix = _ref.prefix,
    label = _ref.label,
    _ref$labelMode = _ref.labelMode,
    labelMode = _ref$labelMode === void 0 ? "top" : _ref$labelMode,
    allowClear = _ref.allowClear,
    onChange = _ref.onChange,
    onFocus = _ref.onFocus,
    onBlur = _ref.onBlur,
    customSize = _ref.size,
    errors = _ref.errors,
    ErrorNode = _ref.ErrorNode,
    warnings = _ref.warnings,
    type = _ref.type,
    htmlType = _ref.htmlType,
    precision = _ref.precision,
    max = _ref.max,
    min = _ref.min,
    _ref$bordered = _ref.bordered,
    bordered = _ref$bordered === void 0 ? true : _ref$bordered,
    props = _objectWithoutProperties(_ref, _excluded);
  var prefixCls = getPrefixCls("input");
  placeholder = label ? label : placeholder;
  var size = React.useContext(SizeContext);
  var mergedSize = customSize || size;
  var mergedAllowClear;
  if (_typeof(allowClear) === "object" && allowClear !== null && allowClear !== void 0 && allowClear.clearIcon) {
    mergedAllowClear = allowClear;
  } else if (allowClear) {
    mergedAllowClear = {
      clearIcon: /*#__PURE__*/_jsx(IconDelete16pxBold, {
        size: 16
      })
    };
  }
  var _useMergedState = useMergedState(props.defaultValue, {
      value: props.value
    }),
    _useMergedState2 = _slicedToArray(_useMergedState, 2),
    value = _useMergedState2[0],
    setValue = _useMergedState2[1];
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    focused = _useState2[0],
    setFocused = _useState2[1];
  var inputRef = useRef(null);
  useImperativeHandle(ref, function () {
    return inputRef.current;
  }, []);
  var focus = function focus(option) {
    var _inputRef$current;
    if ((_inputRef$current = inputRef.current) !== null && _inputRef$current !== void 0 && _inputRef$current.input) {
      triggerFocus(inputRef.current.input, option);
    }
  };
  var handleReset = function handleReset(e) {
    var _inputRef$current2;
    setValue("");
    focus();
    if ((_inputRef$current2 = inputRef.current) !== null && _inputRef$current2 !== void 0 && _inputRef$current2.input) {
      resolveOnChange(inputRef.current.input, e, onChange);
    }
  };
  var handleChange = function handleChange(e) {
    var _inputRef$current3;
    if (type === "number") {
      e.target.value = numberFixed(e.target.value, precision);
    }
    if (props.value === undefined) {
      setValue(e.target.value);
    }
    if ((_inputRef$current3 = inputRef.current) !== null && _inputRef$current3 !== void 0 && _inputRef$current3.input) {
      resolveOnChange(inputRef.current.input, e, onChange);
      // onChange?.(e)
    }
  };

  var handleFocus = useCallback(function (e) {
    setFocused(true);
    onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
  }, [onFocus]);
  var handleBlur = function handleBlur(e) {
    if (type === "number") {
      if (typeof max === "number") {
        if (Number(e.target.value) > max) {
          e.target.value = "".concat(max);
          handleChange(e);
        }
      }
      if (typeof min === "number" && e.target.value) {
        if (min > Number(e.target.value)) {
          e.target.value = "".concat(min);
          handleChange(e);
        }
      }
    }
    setFocused(false);
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
  };
  var getClearIcon = function getClearIcon() {
    var _mergedAllowClear, _cns;
    if (!mergedAllowClear) {
      return null;
    }
    var needClear = !disabled && value;
    var clearIconCls = "".concat(prefixCls, "-clear-icon");
    var iconNode = _typeof(mergedAllowClear) === "object" && (_mergedAllowClear = mergedAllowClear) !== null && _mergedAllowClear !== void 0 && _mergedAllowClear.clearIcon ? mergedAllowClear.clearIcon : "✖";
    return /*#__PURE__*/_jsx("span", {
      onClick: handleReset
      // Do not trigger onBlur when clear input
      // https://github.com/ant-design/ant-design/issues/31200
      ,
      onMouseDown: function onMouseDown(e) {
        return e.preventDefault();
      },
      className: cns(clearIconCls, (_cns = {}, _defineProperty(_cns, "".concat(clearIconCls, "-hidden"), !needClear), _defineProperty(_cns, "".concat(clearIconCls, "-has-suffix"), !!suffix), _cns)),
      role: "button",
      tabIndex: -1,
      children: iconNode
    });
  };
  var suffixNode = (suffix || allowClear) && /*#__PURE__*/_jsxs("span", {
    className: "".concat(prefixCls, "-suffix"),
    children: [getClearIcon(), suffix]
  });
  var referNode = useMemo(function () {
    if (!refer) {
      return;
    }
    var node = refer;
    if (_typeof(refer) === "object" && (refer.label || refer.value)) {
      node = /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("span", {
          className: "".concat(prefixCls, "-refer-label"),
          children: refer.label
        }), /*#__PURE__*/_jsx("span", {
          className: "".concat(prefixCls, "-refer-value"),
          children: refer.value
        })]
      });
    }
    return /*#__PURE__*/_jsx("div", {
      className: "".concat(prefixCls, "-refer-wrap"),
      children: node
    });
  }, [refer]);
  return /*#__PURE__*/_jsxs("div", {
    className: cns("".concat(prefixCls, "-wrap"), (_cns2 = {}, _defineProperty(_cns2, "".concat(prefixCls, "-wrap-status-error"), status === "error"), _defineProperty(_cns2, "".concat(prefixCls, "-wrap-disabled"), disabled), _defineProperty(_cns2, "".concat(prefixCls, "-wrap-has-value"), value !== undefined && value !== "" && value !== null), _defineProperty(_cns2, "".concat(prefixCls, "-affix-wrapper-disabled"), disabled), _defineProperty(_cns2, "".concat(prefixCls, "-wrap-focused"), focused), _defineProperty(_cns2, "".concat(prefixCls, "-wrap-has-refer"), !!referNode), _defineProperty(_cns2, "".concat(prefixCls, "-wrap-lg"), mergedSize === "large"), _defineProperty(_cns2, "".concat(prefixCls, "-wrap-sm"), mergedSize === "small"), _defineProperty(_cns2, "".concat(prefixCls, "-wrap-label-left"), labelMode === "left"), _defineProperty(_cns2, "".concat(prefixCls, "-wrap-label-top"), labelMode === "top"), _defineProperty(_cns2, "".concat(prefixCls, "-wrap-borderless"), !bordered), _cns2), className),
    children: [/*#__PURE__*/_jsxs("div", {
      className: cns("".concat(prefixCls, "-inside-wrap"), {}),
      children: [!!prefix && /*#__PURE__*/_jsx("span", {
        className: "".concat(prefixCls, "-prefix"),
        children: prefix
      }), /*#__PURE__*/_jsx(AInput, _objectSpread(_objectSpread({
        autoComplete: "off",
        ref: inputRef,
        prefixCls: prefixCls,
        className: cns(_defineProperty({}, "".concat(prefixCls, "-hide-placeholder"), !!label)),
        placeholder: placeholder,
        disabled: disabled,
        type: htmlType
      }, props), {}, {
        onFocus: handleFocus,
        onBlur: handleBlur,
        value: value,
        onChange: handleChange
      })), suffixNode, !!label && /*#__PURE__*/_jsx("label", {
        className: "".concat(prefixCls, "-label"),
        children: placeholder
      })]
    }), referNode, /*#__PURE__*/_jsx("fieldset", {
      className: "".concat(prefixCls, "-outline"),
      children: /*#__PURE__*/_jsx("legend", {
        className: cns("".concat(prefixCls, "-label-bg"), _defineProperty({}, "".concat(prefixCls, "-label-bg-none"), !label)),
        children: /*#__PURE__*/_jsx("span", {
          children: placeholder
        })
      })
    })]
  });
});
export var Input = InternalInput;
// Input.Group = AInput.Group;
Input.Password = Password;
Input.TextArea = TextArea;